import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Mail, 
  MessageSquare, 
  Plus, 
  Settings, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
  AlertCircle,
  Server,
  Send,
  Inbox,
  RefreshCw,
  TestTube
} from 'lucide-react';

const ManageExternalServices = () => {
  const { token, getAuthHeaders, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [providerTemplates, setProviderTemplates] = useState({});
  const [serviceConfigs, setServiceConfigs] = useState([]);
  const [activeTab, setActiveTab] = useState('email');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [configFormData, setConfigFormData] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, configId: null, providerName: '' });
  const [testEmailModal, setTestEmailModal] = useState({ show: false, configId: null, providerName: '' });
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // IMAP Settings State
  const [imapSettings, setImapSettings] = useState({
    imap_enabled: false,
    imap_server: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    imap_use_ssl: true,
    imap_mailboxes: ['INBOX'],
    imap_fetch_frequency: 5,
    imap_auto_cleanup: false,
    imap_cleanup_age_days: 7,
    imap_reply_to_type: 'alias', // 'alias' or 'plus_addressing'
    imap_reply_to_base_email: '' // Base email for reply-to (e.g., support@yibooking.com)
  });
  const [imapPresets, setImapPresets] = useState({});
  const [savingImap, setSavingImap] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [showImapPassword, setShowImapPassword] = useState(false);
  const [detectedMailboxes, setDetectedMailboxes] = useState([]);
  const [imapTestResult, setImapTestResult] = useState(null); // { success: bool, message: string }
  const [pollingImap, setPollingImap] = useState(false);
  const [pollResult, setPollResult] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, configsRes, imapPresetsRes, globalImapRes] = await Promise.all([
        fetch(`${API_URL}/api/external-services/providers`, {
          headers: { ...getAuthHeaders() }
        }),
        fetch(`${API_URL}/api/external-services/`, {
          headers: { ...getAuthHeaders() }
        }),
        fetch(`${API_URL}/api/email-settings/imap-presets`, {
          headers: { ...getAuthHeaders() }
        }),
        fetch(`${API_URL}/api/email-settings/global-imap`, {
          headers: { ...getAuthHeaders() }
        })
      ]);

      if (!templatesRes.ok || !configsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const templates = await templatesRes.json();
      const configs = await configsRes.json();

      setProviderTemplates(templates);
      setServiceConfigs(configs);
      
      if (imapPresetsRes.ok) {
        const presets = await imapPresetsRes.json();
        setImapPresets(presets);
      }
      
      if (globalImapRes.ok) {
        const imapData = await globalImapRes.json();
        if (imapData.success) {
          setImapSettings(prev => ({
            ...prev,
            ...imapData.data,
            imap_password: '' // Don't show actual password
          }));
        }
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const openConfigModal = (providerType, providerKey, existingConfig = null) => {
    const template = providerTemplates[providerType]?.[providerKey];
    if (!template) return;

    setSelectedProvider({
      type: providerType,
      key: providerKey,
      ...template
    });

    // Pre-fill form with default values from template fields, then override with existing config
    const defaultValues = {};
    template.fields.forEach(field => {
      if (field.default !== undefined) {
        defaultValues[field.key] = field.default;
      }
    });

    if (existingConfig) {
      setEditingConfig(existingConfig);
      setConfigFormData({ ...defaultValues, ...existingConfig.config });
    } else {
      setEditingConfig(null);
      setConfigFormData(defaultValues);
    }

    setShowPasswords({});
    setShowConfigModal(true);
  };

  const handleFormChange = (key, value) => {
    // Special handling for TLS/SSL - they should be mutually exclusive
    if (key === 'use_tls' && value === true) {
      setConfigFormData(prev => ({
        ...prev,
        use_tls: true,
        use_ssl: false
      }));
    } else if (key === 'use_ssl' && value === true) {
      setConfigFormData(prev => ({
        ...prev,
        use_tls: false,
        use_ssl: true
      }));
    } else {
      setConfigFormData(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const togglePasswordVisibility = (key) => {
    setShowPasswords(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveConfig = async (setAsActive = false) => {
    if (!selectedProvider) return;

    // Build final config with defaults
    const finalConfig = {};
    selectedProvider.fields.forEach(field => {
      const value = configFormData[field.key] ?? field.default;
      if (value !== undefined && value !== '') {
        finalConfig[field.key] = value;
      }
    });

    // Validate required fields against final config
    const requiredFields = selectedProvider.fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !finalConfig[f.key]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        provider_type: selectedProvider.type,
        provider_name: selectedProvider.key,
        is_active: setAsActive,
        config: finalConfig
      };

      let response;
      if (editingConfig) {
        // Update existing config
        response = await fetch(`${API_URL}/api/external-services/${editingConfig.id}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            is_active: setAsActive,
            config: configFormData
          })
        });
      } else {
        // Create new config
        response = await fetch(`${API_URL}/api/external-services/`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save configuration');
      }

      setSuccess(`${selectedProvider.name} configuration saved successfully!`);
      setShowConfigModal(false);
      fetchData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (config) => {
    try {
      const response = await fetch(`${API_URL}/api/external-services/${config.id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !config.is_active })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      setSuccess(`${config.provider_name} ${!config.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (configId) => {
    try {
      const response = await fetch(`${API_URL}/api/external-services/${configId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete configuration');
      }

      setSuccess('Configuration deleted successfully!');
      setDeleteModal({ show: false, configId: null, providerName: '' });
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setDeleteModal({ show: false, configId: null, providerName: '' });
    }
  };

  const getConfigForProvider = (providerType, providerKey) => {
    return serviceConfigs.find(
      c => c.provider_type === providerType && c.provider_name === providerKey
    );
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmailModal.configId) return;

    setSendingTest(true);
    setTestResult(null);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/external-services/${testEmailModal.configId}/test`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ to_email: testEmail })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTestResult({
          success: true,
          message: data.message,
          details: data.details
        });
      } else {
        setTestResult({
          success: false,
          message: data.detail || 'Failed to send test email'
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: err.message || 'An error occurred while sending test email'
      });
    } finally {
      setSendingTest(false);
    }
  };

  const openTestEmailModal = (config, providerName) => {
    setTestEmailModal({
      show: true,
      configId: config.id,
      providerName: providerName
    });
    setTestEmail('');
    setTestResult(null);
  };

  const getActiveProvider = (providerType) => {
    return serviceConfigs.find(
      c => c.provider_type === providerType && c.is_active
    );
  };

  // IMAP Configuration Functions
  const applyImapPreset = (presetKey) => {
    const preset = imapPresets[presetKey];
    if (preset) {
      setImapSettings(prev => ({
        ...prev,
        imap_server: preset.imap_server,
        imap_port: preset.imap_port,
        imap_use_ssl: preset.imap_use_ssl,
        imap_reply_to_type: preset.imap_reply_to_type || 'alias'
      }));
      setImapTestResult(null); // Clear test result when settings change
    }
  };

  const handleImapSettingChange = (field, value) => {
    setImapSettings(prev => ({ ...prev, [field]: value }));
    setImapTestResult(null); // Clear test result when settings change
  };

  const handleSaveImap = async () => {
    setSavingImap(true);
    setError('');

    // Preserve the current password before any operations
    const currentPassword = imapSettings.imap_password;

    try {
      const response = await fetch(`${API_URL}/api/email-settings/global-imap`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(imapSettings)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save IMAP settings');
      }

      setSuccess('Global IMAP settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Refresh to get updated state but preserve password
      const globalImapRes = await fetch(`${API_URL}/api/email-settings/global-imap`, {
        headers: { ...getAuthHeaders() }
      });
      
      if (globalImapRes.ok) {
        const imapData = await globalImapRes.json();
        if (imapData.success) {
          setImapSettings(prev => ({
            ...prev,
            ...imapData.data,
            // Keep the password user entered (don't clear it)
            imap_password: currentPassword
          }));
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingImap(false);
    }
  };

  const handleTestImap = async () => {
    setTestingImap(true);
    setError('');
    setImapTestResult(null);

    // Preserve the current password before any operations
    const currentPassword = imapSettings.imap_password;

    try {
      // First save the settings silently
      const saveResponse = await fetch(`${API_URL}/api/email-settings/global-imap`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(imapSettings)
      });

      if (!saveResponse.ok) {
        const saveData = await saveResponse.json();
        throw new Error(saveData.detail || 'Failed to save IMAP settings before testing');
      }

      // Now test the connection
      const response = await fetch(`${API_URL}/api/email-settings/test-global-imap`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        setImapTestResult({
          success: true,
          message: data.message || 'Connection successful!'
        });
        if (data.mailboxes) {
          setDetectedMailboxes(data.mailboxes);
        }
      } else {
        setImapTestResult({
          success: false,
          message: data.message || 'Connection failed'
        });
      }

      // Refresh data to update UI state (like "Configured" badge)
      // But preserve the password the user entered
      const [globalImapRes] = await Promise.all([
        fetch(`${API_URL}/api/email-settings/global-imap`, {
          headers: { ...getAuthHeaders() }
        })
      ]);
      
      if (globalImapRes.ok) {
        const imapData = await globalImapRes.json();
        if (imapData.success) {
          setImapSettings(prev => ({
            ...prev,
            ...imapData.data,
            // Keep the password user entered (don't clear it)
            imap_password: currentPassword
          }));
        }
      }
    } catch (err) {
      setImapTestResult({
        success: false,
        message: err.message || 'An error occurred during testing'
      });
    } finally {
      setTestingImap(false);
    }
  };

  const handlePollImap = async () => {
    setPollingImap(true);
    setPollResult(null);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/email-settings/poll-imap`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      setPollResult(data);

      if (data.success && data.processed > 0) {
        setSuccess(`Successfully processed ${data.processed} inbound email(s)!`);
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      setPollResult({
        success: false,
        error: err.message || 'Failed to poll IMAP'
      });
    } finally {
      setPollingImap(false);
    }
  };

  const renderProviderCard = (providerType, providerKey, template) => {
    const existingConfig = getConfigForProvider(providerType, providerKey);
    const isActive = existingConfig?.is_active;
    const isConfigured = !!existingConfig;
    const isSmtp = providerType === 'smtp';

    return (
      <div
        key={providerKey}
        className={`bg-white rounded-lg shadow-md p-6 border-2 transition-all ${
          isActive ? 'border-green-500' : isConfigured ? 'border-blue-200' : 'border-gray-200'
        }`}
        data-testid={`provider-card-${providerKey}`}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-800">{template.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
          </div>
          <div className="flex items-center gap-2">
            {isActive && (
              <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Active
              </span>
            )}
            {isConfigured && !isActive && (
              <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full">
                Configured
              </span>
            )}
          </div>
        </div>

        {/* Only show "Get API Key" link for non-SMTP providers that are not configured */}
        {!isConfigured && !isSmtp && template.signup_url && (
          <a
            href={template.signup_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-4"
          >
            Get API Key <ExternalLink className="w-3 h-3" />
          </a>
        )}

        <div className="flex flex-wrap gap-2">
          {isConfigured ? (
            <>
              <button
                onClick={() => openConfigModal(providerType, providerKey, existingConfig)}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors flex items-center justify-center gap-2"
                data-testid={`edit-${providerKey}-btn`}
              >
                <Settings className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => handleToggleActive(existingConfig)}
                className={`flex-1 ${
                  isActive
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : 'bg-green-500 hover:bg-green-600'
                } text-white px-4 py-2 rounded transition-colors flex items-center justify-center gap-2`}
                data-testid={`toggle-${providerKey}-btn`}
              >
                {isActive ? (
                  <>
                    <XCircle className="w-4 h-4" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Activate
                  </>
                )}
              </button>
              {/* Send Test Email button - only for SMTP and email providers */}
              {(providerType === 'smtp' || providerType === 'email') && (
                <button
                  onClick={() => openTestEmailModal(existingConfig, template.name)}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded transition-colors flex items-center justify-center gap-1"
                  title="Send Test Email"
                  data-testid={`test-${providerKey}-btn`}
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setDeleteModal({ 
                  show: true, 
                  configId: existingConfig.id, 
                  providerName: template.name 
                })}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded transition-colors"
                data-testid={`delete-${providerKey}-btn`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => openConfigModal(providerType, providerKey)}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded transition-colors flex items-center justify-center gap-2"
              data-testid={`configure-${providerKey}-btn`}
            >
              <Plus className="w-4 h-4" />
              Configure
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex justify-center items-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <section className="py-20 bg-gradient-to-br from-white to-amber-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Manage External Services</h1>
            <p className="text-gray-600">Configure Email and SMS providers for notifications</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {success}
            </div>
          )}

          {/* Active Provider Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Email API</h3>
                  <p className="text-sm text-gray-600">
                    {getActiveProvider('email') ? (
                      <span className="text-green-600 font-medium">
                        {providerTemplates.email?.[getActiveProvider('email').provider_name]?.name || 'Unknown'} (Active)
                      </span>
                    ) : (
                      <span className="text-orange-600">Not configured</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Server className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">SMTP Server</h3>
                  <p className="text-sm text-gray-600">
                    {getActiveProvider('smtp') ? (
                      <span className="text-green-600 font-medium">
                        {providerTemplates.smtp?.[getActiveProvider('smtp').provider_name]?.name || 'Unknown'} (Active)
                      </span>
                    ) : (
                      <span className="text-orange-600">Not configured</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">SMS Provider</h3>
                  <p className="text-sm text-gray-600">
                    {getActiveProvider('sms') ? (
                      <span className="text-green-600 font-medium">
                        {providerTemplates.sms?.[getActiveProvider('sms').provider_name]?.name || 'Unknown'} (Active)
                      </span>
                    ) : (
                      <span className="text-orange-600">Not configured</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Email API vs SMTP:</strong> Email API providers (SendGrid, etc.) offer better deliverability and analytics. 
              SMTP is for using your existing email service (Hostinger, Gmail, etc.). Only one email method can be active at a time.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-4 mb-6">
            <button
              onClick={() => setActiveTab('email')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'email'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="email-tab"
            >
              <Mail className="w-5 h-5" />
              Email API
            </button>
            <button
              onClick={() => setActiveTab('smtp')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'smtp'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="smtp-tab"
            >
              <Server className="w-5 h-5" />
              SMTP Server
            </button>
            <button
              onClick={() => setActiveTab('imap')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'imap'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="imap-tab"
            >
              <Inbox className="w-5 h-5" />
              IMAP Inbound
            </button>
            <button
              onClick={() => setActiveTab('sms')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'sms'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="sms-tab"
            >
              <MessageSquare className="w-5 h-5" />
              SMS Providers
            </button>
          </div>

          {/* IMAP Configuration Panel */}
          {activeTab === 'imap' && (
            <div className="bg-white rounded-lg shadow-md p-6 border-2 border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <Inbox className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-800">Global Inbound Email (IMAP)</h2>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Configure system-wide IMAP settings to receive customer email replies into the Message Console. Calendar Admins can override these settings in their Email Settings for custom branding.
              </p>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="global_imap_enabled"
                  checked={imapSettings.imap_enabled}
                  onChange={(e) => handleImapSettingChange('imap_enabled', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="global_imap_enabled" className="text-sm font-medium text-gray-700">
                  Enable Global Inbound Email
                </label>
                {imapSettings.imap_configured && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Configured
                  </span>
                )}
              </div>

              {imapSettings.imap_enabled && (
                <>
                  {/* IMAP Presets */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quick Setup - Select Email Provider
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(imapPresets).map(([key, preset]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => applyImapPreset(key)}
                          className="px-3 py-1 bg-purple-100 hover:bg-purple-200 rounded text-sm font-medium transition-colors text-purple-700"
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IMAP Server *</label>
                      <input
                        type="text"
                        value={imapSettings.imap_server}
                        onChange={(e) => handleImapSettingChange('imap_server', e.target.value)}
                        placeholder="imap.hostinger.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Port *</label>
                      <input
                        type="number"
                        value={imapSettings.imap_port}
                        onChange={(e) => handleImapSettingChange('imap_port', parseInt(e.target.value))}
                        placeholder="993"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email/Username *</label>
                      <input
                        type="email"
                        value={imapSettings.imap_username}
                        onChange={(e) => handleImapSettingChange('imap_username', e.target.value)}
                        placeholder="support@yibooking.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                      <div className="relative">
                        <input
                          type={showImapPassword ? 'text' : 'password'}
                          value={imapSettings.imap_password}
                          onChange={(e) => handleImapSettingChange('imap_password', e.target.value)}
                          placeholder={imapSettings.imap_password_set ? '••••••••' : 'Enter password'}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowImapPassword(!showImapPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showImapPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fetch Frequency</label>
                      <select
                        value={imapSettings.imap_fetch_frequency}
                        onChange={(e) => handleImapSettingChange('imap_fetch_frequency', parseInt(e.target.value))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value={1}>Every 1 minute</option>
                        <option value={2}>Every 2 minutes</option>
                        <option value={5}>Every 5 minutes</option>
                        <option value={10}>Every 10 minutes</option>
                        <option value={15}>Every 15 minutes</option>
                        <option value={30}>Every 30 minutes</option>
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="global_imap_ssl"
                        checked={imapSettings.imap_use_ssl}
                        onChange={(e) => handleImapSettingChange('imap_use_ssl', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="global_imap_ssl" className="ml-2 text-sm text-gray-700">Use SSL (recommended)</label>
                    </div>
                  </div>

                  {/* Mailboxes */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mailboxes to Monitor</label>
                    <input
                      type="text"
                      value={imapSettings.imap_mailboxes?.join(', ')}
                      onChange={(e) => {
                        const mailboxes = e.target.value.split(',').map(m => m.trim()).filter(m => m);
                        handleImapSettingChange('imap_mailboxes', mailboxes.length > 0 ? mailboxes : ['INBOX']);
                      }}
                      placeholder="INBOX, Support"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Comma-separated list of mailbox names</p>
                    {detectedMailboxes.length > 0 && (
                      <div className="mt-2 text-xs text-purple-600">
                        Detected mailboxes: {detectedMailboxes.slice(0, 10).join(', ')}
                      </div>
                    )}
                  </div>

                  {/* Reply-To Address Configuration */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Reply-To Address Configuration
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reply-To Type</label>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                            <input
                              type="radio"
                              name="reply_to_type"
                              value="alias"
                              checked={imapSettings.imap_reply_to_type === 'alias'}
                              onChange={(e) => handleImapSettingChange('imap_reply_to_type', e.target.value)}
                              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div>
                              <span className="font-medium text-gray-900">Alias</span>
                              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Recommended for Hostinger</span>
                              <p className="text-xs text-gray-500 mt-1">
                                Uses calendar symbolic name as email: <code className="bg-gray-100 px-1 rounded">{'{calendar-name}'}@yibooking.com</code>
                                <br />Requires manual alias setup in your email provider.
                              </p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                            <input
                              type="radio"
                              name="reply_to_type"
                              value="plus_addressing"
                              checked={imapSettings.imap_reply_to_type === 'plus_addressing'}
                              onChange={(e) => handleImapSettingChange('imap_reply_to_type', e.target.value)}
                              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div>
                              <span className="font-medium text-gray-900">Plus Addressing</span>
                              <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Gmail, Outlook</span>
                              <p className="text-xs text-gray-500 mt-1">
                                Uses encoded tags: <code className="bg-gray-100 px-1 rounded">support+{'{encoded-tag}'}@domain.com</code>
                                <br />Requires email provider that supports + addressing.
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {imapSettings.imap_reply_to_type === 'alias' && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-sm text-yellow-800">
                            <strong>Setup Required:</strong> For each calendar, create an email alias in your email provider:
                          </p>
                          <ul className="text-xs text-yellow-700 mt-2 list-disc list-inside">
                            <li>Example: <code className="bg-yellow-100 px-1 rounded">first-calendar@yibooking.com</code></li>
                            <li>The alias should forward to your main inbox</li>
                            <li>In Hostinger: Email → Aliases → Create Alias</li>
                          </ul>
                        </div>
                      )}

                      {imapSettings.imap_reply_to_type === 'plus_addressing' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Base Email for Plus Addressing</label>
                          <input
                            type="email"
                            value={imapSettings.imap_reply_to_base_email || ''}
                            onChange={(e) => handleImapSettingChange('imap_reply_to_base_email', e.target.value)}
                            placeholder="support@yibooking.com"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            This email will be used as: <code className="bg-gray-100 px-1 rounded">{imapSettings.imap_reply_to_base_email || 'support'}+tag@domain.com</code>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Auto Cleanup Settings */}
                  <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      Auto-Cleanup Settings
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="imap_auto_cleanup"
                          checked={imapSettings.imap_auto_cleanup}
                          onChange={(e) => handleImapSettingChange('imap_auto_cleanup', e.target.checked)}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <label htmlFor="imap_auto_cleanup" className="ml-2 text-sm text-gray-700">
                          Enable auto-cleanup of old read emails
                        </label>
                      </div>
                      
                      {imapSettings.imap_auto_cleanup && (
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-700">Delete read emails older than</label>
                          <select
                            value={imapSettings.imap_cleanup_age_days}
                            onChange={(e) => handleImapSettingChange('imap_cleanup_age_days', parseInt(e.target.value))}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          >
                            <option value={1}>1 day</option>
                            <option value={3}>3 days</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                            <option value={60}>60 days</option>
                            <option value={90}>90 days</option>
                          </select>
                        </div>
                      )}
                      
                      <p className="text-xs text-orange-700">
                        When enabled, processed (read) emails older than the specified age will be permanently deleted from your IMAP server during each poll.
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleSaveImap}
                      disabled={savingImap}
                      className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {savingImap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
                      Save IMAP Settings
                    </button>
                    <button
                      onClick={handleTestImap}
                      disabled={testingImap || !imapSettings.imap_server}
                      className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {testingImap ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                      Test Connection
                    </button>
                  </div>

                  {/* IMAP Test Result Display */}
                  {imapTestResult && (
                    <div className={`mt-4 p-4 rounded-lg border-2 ${
                      imapTestResult.success 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-red-50 border-red-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        {imapTestResult.success ? (
                          <div className="p-2 bg-green-100 rounded-full">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          </div>
                        ) : (
                          <div className="p-2 bg-red-100 rounded-full">
                            <XCircle className="w-6 h-6 text-red-600" />
                          </div>
                        )}
                        <div>
                          <h4 className={`font-semibold ${
                            imapTestResult.success ? 'text-green-800' : 'text-red-800'
                          }`}>
                            {imapTestResult.success ? 'Connection Verified!' : 'Connection Failed'}
                          </h4>
                          <p className={`text-sm ${
                            imapTestResult.success ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {imapTestResult.message}
                          </p>
                        </div>
                        <button
                          onClick={() => setImapTestResult(null)}
                          className="ml-auto text-gray-400 hover:text-gray-600"
                          title="Dismiss"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Poll for Inbound Emails Section */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-purple-600" />
                      Fetch Inbound Emails
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Manually poll the IMAP server to fetch new inbound emails. In production, this runs automatically based on the fetch frequency setting.
                    </p>
                    
                    <button
                      onClick={handlePollImap}
                      disabled={pollingImap || !imapSettings.imap_enabled || !imapSettings.imap_configured}
                      className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {pollingImap ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Poll Now
                    </button>

                    {/* Poll Result Display */}
                    {pollResult && (
                      <div className={`mt-4 p-4 rounded-lg border ${
                        pollResult.success ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className={`font-semibold ${pollResult.success ? 'text-blue-800' : 'text-red-800'}`}>
                              {pollResult.success ? 'Poll Complete' : 'Poll Failed'}
                            </h4>
                            {pollResult.success ? (
                              <div className="text-sm text-blue-700 mt-1">
                                <p>Fetched: {pollResult.total_fetched || 0} email(s)</p>
                                <p>Processed: {pollResult.processed || 0} email(s)</p>
                                {pollResult.errors && pollResult.errors.length > 0 && (
                                  <details className="mt-2">
                                    <summary className="cursor-pointer text-orange-600">
                                      {pollResult.errors.length} email(s) could not be routed
                                    </summary>
                                    <ul className="mt-1 text-xs text-gray-600 list-disc list-inside max-h-32 overflow-auto">
                                      {pollResult.errors.slice(0, 10).map((err, i) => (
                                        <li key={i}>{err}</li>
                                      ))}
                                      {pollResult.errors.length > 10 && (
                                        <li>...and {pollResult.errors.length - 10} more</li>
                                      )}
                                    </ul>
                                  </details>
                                )}
                                {pollResult.cleanup && pollResult.cleanup.deleted > 0 && (
                                  <p className="mt-2 text-orange-600 flex items-center gap-1">
                                    <Trash2 className="w-3 h-3" />
                                    Cleanup: {pollResult.cleanup.deleted} old email(s) deleted
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-red-700 mt-1">{pollResult.error}</p>
                            )}
                          </div>
                          <button
                            onClick={() => setPollResult(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Provider Cards (for email, smtp, sms tabs) */}
          {activeTab !== 'imap' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {providerTemplates[activeTab] && 
                Object.entries(providerTemplates[activeTab]).map(([key, template]) =>
                  renderProviderCard(activeTab, key, template)
                )
              }
            </div>
          )}
        </div>
      </section>
      <Footer />

      {/* Configuration Modal */}
      {showConfigModal && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Configure {selectedProvider.name}
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              {selectedProvider.description}
            </p>

            {/* Only show setup link for non-SMTP providers */}
            {selectedProvider.signup_url && selectedProvider.type !== 'smtp' && (
              <a
                href={selectedProvider.signup_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-6"
              >
                Get setup instructions for {selectedProvider.name} <ExternalLink className="w-3 h-3" />
              </a>
            )}

            <div className="space-y-4">
              {selectedProvider.fields.map((field) => (
                <div key={field.key}>
                  {field.type === 'checkbox' ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={configFormData[field.key] ?? field.default ?? false}
                        onChange={(e) => handleFormChange(field.key, e.target.checked)}
                        className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        data-testid={`input-${field.key}`}
                      />
                      <span className="text-sm font-medium text-gray-700">{field.label}</span>
                    </label>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <div className="relative">
                        <input
                          type={field.type === 'password' && !showPasswords[field.key] ? 'password' : (field.type === 'number' ? 'number' : 'text')}
                          value={configFormData[field.key] ?? field.default ?? ''}
                          onChange={(e) => handleFormChange(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-10"
                          data-testid={`input-${field.key}`}
                        />
                        {field.type === 'password' && (
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility(field.key)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showPasswords[field.key] ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={() => handleSaveConfig(true)}
                disabled={saving}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-3 rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="save-and-activate-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Save & Activate
              </button>
              <button
                onClick={() => handleSaveConfig(false)}
                disabled={saving}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-3 rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="save-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Settings className="w-4 h-4" />
                )}
                Save (Do Not Activate)
              </button>
              <button
                onClick={() => {
                  setShowConfigModal(false);
                  setError('');
                }}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-4 py-3 rounded transition-colors"
                data-testid="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the {deleteModal.providerName} configuration? 
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, configId: null, providerName: '' })}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-4 py-2 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModal.configId)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold px-4 py-2 rounded transition-colors"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {testEmailModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Send className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Send Test Email</h3>
                <p className="text-sm text-gray-600">{testEmailModal.providerName}</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-4">
              Enter an email address to send a test email and verify your configuration is working correctly.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="test-email-input"
              />
            </div>

            {testResult && (
              <div className={`mb-4 p-4 rounded-lg ${
                testResult.success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-semibold ${
                    testResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {testResult.success ? 'Test Successful!' : 'Test Failed'}
                  </span>
                </div>
                <p className={`text-sm ${
                  testResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {testResult.message}
                </p>
                {testResult.success && testResult.details && (
                  <div className="mt-2 text-xs text-green-600">
                    <p>Server: {testResult.details.server}</p>
                    <p>Port: {testResult.details.port}</p>
                    <p>From: {testResult.details.from}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setTestEmailModal({ show: false, configId: null, providerName: '' });
                  setTestEmail('');
                  setTestResult(null);
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-4 py-2 rounded transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleSendTestEmail}
                disabled={sendingTest || !testEmail}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-semibold px-4 py-2 rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-test-btn"
              >
                {sendingTest ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageExternalServices;
